/**
 * Shared beforeunload guard for in-flight report generation.
 *
 * Persists a reportDialogDraft snapshot on page unload and optionally
 * prompts the user before navigating away during the generating step.
 */
import { useEffect } from 'react';
import {
  isNonTrivialDraft,
  saveReportDialogDraft,
  type ReportDialogDraft,
} from './reportDialogDraft';

const RELOAD_WARN_MESSAGE =
  'Report generation is in progress. Leaving now may interrupt it.';

export interface UseGenerationBeforeUnloadGuardArgs {
  /** When false, no handlers are registered. */
  enabled: boolean;
  /** When true, browser shows a leave confirmation dialog. */
  warnOnNavigate: boolean;
  siteId: string;
  reportType: string | null;
  step: ReportDialogDraft['step'];
  mode?: ReportDialogDraft['mode'];
  existingReportId?: string;
  dirtyFields?: ReadonlyArray<'preferences' | 'documents' | 'archetype'>;
  runId?: string | null;
}

export function useGenerationBeforeUnloadGuard({
  enabled,
  warnOnNavigate,
  siteId,
  reportType,
  step,
  mode,
  existingReportId,
  dirtyFields,
  runId,
}: UseGenerationBeforeUnloadGuardArgs): void {
  const buildSnapshot = (): ReportDialogDraft => ({
    savedAt: Date.now(),
    siteId,
    reportType: reportType!,
    step,
    ...(mode ? { mode } : {}),
    ...(existingReportId ? { existingReportId } : {}),
    ...(dirtyFields && dirtyFields.length > 0 ? { dirtyFields } : {}),
    ...(runId ? { runId } : {}),
  });

  // Eager persist — survives wizard background-close and unreliable beforeunload.
  useEffect(() => {
    if (!enabled || !reportType) return;
    const snapshot = buildSnapshot();
    if (isNonTrivialDraft(snapshot)) {
      saveReportDialogDraft(snapshot);
    }
  }, [
    enabled,
    siteId,
    reportType,
    step,
    mode,
    existingReportId,
    dirtyFields,
    runId,
  ]);

  useEffect(() => {
    if (!enabled || !reportType) return;

    const handler = (event: BeforeUnloadEvent) => {
      const snapshot = buildSnapshot();
      if (isNonTrivialDraft(snapshot)) {
        saveReportDialogDraft(snapshot);
      }
      if (warnOnNavigate) {
        event.preventDefault();
        event.returnValue = RELOAD_WARN_MESSAGE;
        return RELOAD_WARN_MESSAGE;
      }
    };

    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [
    enabled,
    warnOnNavigate,
    siteId,
    reportType,
    step,
    mode,
    existingReportId,
    dirtyFields,
    runId,
  ]);
}
